import {
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
} from "@mui/icons-material";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AllergyAlertCard from "screens/patientInfo/components/sidebarComponents/AllergyAlertCard";
import MedicalConditionCard from "screens/patientInfo/components/sidebarComponents/MedicalConditionCard";
import MedicationAccordion from "screens/patientInfo/components/sidebarComponents/MedicationAccordion";
import VitalsDisplay from "screens/patientInfo/components/sidebarComponents/VitalDisplay";

// Main component
const EhrPatientDataSidebar = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Mobile
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg")); // Laptop
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // Desktop

  const { i18n } = useTranslation();
  const app = useSelector((state) => state.app);
  const themeMode = app.theme; // "dark" || "light"

  const [isOpen, setIsOpen] = useState(false);
  const [rerender, setRerender] = useState(0);
  const [disableHistorySidebar, setDisableHistorySidebar] = useState(false);

  const toggleSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isOpen) {
      setRerender((prev) => prev + 1);
    }
  }, [isOpen]);

  // Responsive values based on screen size
  const getSidebarWidth = () => {
    if (isMobile) return "90vw";
    if (isTablet) return "70vw";
    if (isLaptop) return "50vw";
    return "40vw"; // Desktop default
  };

  const isDarkTheme = app?.theme === "dark";
  const { radius, boxShadow } = styles.shared;

  return (
    <>
      {/* Overlay when sidebar is open */}
      {isOpen && <div onClick={toggleSidebar} style={styles.overlay} />}

      {/* Main Sidebar */}
      <div
        style={styles.sidebar(
          isDarkTheme,
          isOpen,
          getSidebarWidth,
          isMobile,
          radius,
          boxShadow
        )}
      >
        <div style={styles.header(isMobile)}>
          <button style={styles.closeButton(isMobile)} onClick={toggleSidebar}>
            <ArrowForwardIosOutlined
              fontSize={isMobile ? "small" : "medium"}
              style={{ alignSelf: "center" }}
            />
          </button>
          <div style={styles.buttonText}>
            <Typography style={styles.sidebarText(isMobile)}>
              EHR Patient Data
            </Typography>
          </div>
        </div>
        <EhrPatientSideBarContent
          setDisableHistorySidebar={setDisableHistorySidebar}
          scrollRender={rerender}
          isMobile={isMobile}
        />
      </div>

      {/* Toggle Button */}
      <div
        onClick={toggleSidebar}
        style={styles.toggleButton(
          disableHistorySidebar,
          isDarkTheme,
          radius,
          boxShadow,
          isMobile
        )}
        className="overlay-wrapper"
      >
        <button style={styles.toggleIcon(isMobile, isDarkTheme)}>
          <ArrowBackIosNewOutlined fontSize={isMobile ? "small" : "medium"} />
        </button>

        <div style={styles.toggleTextWrapper}>
          <Typography sx={styles.toggleText(isMobile, isDarkTheme)}>
            EHR Patient Data
          </Typography>
        </div>
      </div>
    </>
  );
};

// Content component
const EhrPatientSideBarContent = ({ isMobile, scrollRender }) => {
  const {
    app,
    encounterObj: { consolidatedNote },
  } = useSelector((state) => state);
  const isDarkTheme = app?.theme === "dark";
  const patientData = consolidatedNote?.section
    ?.map((item) => {
      try {
        return item?.title ? JSON.parse(item.title) : null;
      } catch (error) {
        console.warn("Failed to parse item.title:", item?.title, error);
        return null;
      }
    })
    .filter(Boolean);

  // Extract data categories
  const allergies = patientData?.[0] || [];
  const medications = patientData?.[1] || [];
  const vitalSigns = patientData?.[2] || [];
  const conditionsProblemList = patientData?.[3] || [];
  const conditionsDiagnosis = patientData?.[4] || [];
  const conditions = [...conditionsDiagnosis, ...conditionsProblemList];

  return (
    <div style={styles.contentContainer(isMobile, isDarkTheme)}>
      <section style={styles.section(isMobile)}>
        <h5 style={styles.heading(isMobile, isDarkTheme)}>
          {!conditions?.length ? "No Active" : ""} Conditions
        </h5>
        <div style={styles.itemsContainer}>
          {conditions?.length
            ? conditions?.map((condition, index) => {
                const conditionProps = {
                  diagnosis: condition?.conditionName || undefined,
                  verification: condition?.verificationStatus || undefined,
                  status: condition?.clinicalStatus || undefined,
                  onsetDate: condition?.onsetDate || undefined,
                  severity: condition?.severity || undefined,
                  category: condition?.categories?.join(", ") || undefined,
                  isDarkTheme: isDarkTheme,
                };
                return <MedicalConditionCard key={index} {...conditionProps} />;
              })
            : null}
        </div>
      </section>

      <section style={styles.section(isMobile)}>
        <h5 style={styles.heading(isMobile, isDarkTheme)}>
          {!medications?.length ? "No Active" : ""} Medications
        </h5>
        <div style={styles.itemsContainer}>
          {medications?.length
            ? medications?.map((medication, index) => {
                const medicationItem = {
                  prescribed: medication?.authoredOn,
                  dosageInstructions: medication?.dosageInstructions?.text,
                  dosage: `${medication?.dosageInstructions.doseQuantity} ${medication?.dosageInstructions.doseUnit}`,
                  medicationName: medication?.medicationName,
                  prescriber: medication?.prescriber,
                  status: medication?.status,
                  asNeeded: medication?.dosageInstructions.asNeeded,
                  therapyType: medication?.therapyType,
                  frequency: medication?.dosageInstructions?.frequency,
                  route: medication?.dosageInstructions?.route,
                  duration: `${medication?.supply?.expectedDuration?.value} ${medication?.supply?.expectedDuration?.unit}`,
                  quantity: `${medication?.supply.quantity} ${medication?.supply?.unit}`,
                  refills: medication?.supply?.refills,
                  isMobile: isMobile,
                  isDarkTheme: isDarkTheme,
                };
                return <MedicationAccordion key={index} {...medicationItem} />;
              })
            : null}
        </div>
      </section>

      <section style={styles.section(isMobile)}>
        <h5 style={styles.heading(isMobile, isDarkTheme)}>
          {!allergies?.length ? "No Active" : ""} Allergies
        </h5>
        <div style={styles.itemsContainer}>
          {allergies?.length
            ? allergies?.map((item, index) => {
                const reactionString =
                  item?.reactions
                    ?.map((reaction) => reaction?.manifestations?.join(", "))
                    ?.join(", ") || "Unknown";

                const allergyProps = {
                  allergyName: item.allergyName,
                  reaction: reactionString,
                  verification: item.verificationStatus,
                  onsetDate: item.onsetDate,
                  ...item,
                  isMobile: isMobile,
                  isDarkTheme: isDarkTheme,
                };

                return <AllergyAlertCard key={index} {...allergyProps} />;
              })
            : null}
        </div>
      </section>

      <section style={styles.section(isMobile)}>
        <h5 style={styles.heading(isMobile, isDarkTheme)}>
          {!vitalSigns?.length ? "No Recorded" : ""} Vitals
        </h5>
        <div>
          {vitalSigns?.length ? (
            <VitalsDisplay
              vitalsData={vitalSigns}
              isMobile={isMobile}
              isDarkTheme={isDarkTheme}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
};

// Style objects
const styles = {
  // Shared styles
  shared: {
    radius: "1rem",
    boxShadow: "-2px 4px 4px #5D5FEF33",
  },

  // Overlay styles
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1400,
    transition: "ease-in-out",
  },

  // Sidebar styles
  sidebar: (
    isDarkTheme,
    isOpen,
    getSidebarWidth,
    isMobile,
    radius,
    boxShadow
  ) => ({
    position: "fixed",
    top: "10%",
    right: 0,
    height: "80vh",
    width: getSidebarWidth(),
    borderRadius: radius,
    backgroundColor: isDarkTheme ? "#414079" : "#f9fafc",
    zIndex: 1401,
    boxShadow: boxShadow,
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column",
    padding: isMobile ? "0.75rem" : "1rem",
    gap: isMobile ? "0.75rem" : "1rem",
    overflow: "hidden",
  }),

  // Header styles
  header: (isMobile) => ({
    display: "flex",
    flexDirection: "row",
    gap: isMobile ? "0.75rem" : "1.25rem",
    alignItems: "center",
  }),

  // Button styles
  closeButton: (isMobile) => ({
    width: isMobile ? "1.5rem" : "1.625rem",
    height: isMobile ? "1.5rem" : "1.625rem",
    backgroundColor: "#5D5FEF",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "none",
    color: "#fff",
    padding: 0,
  }),

  buttonText: {
    backgroundColor: "#5D5FEF",
    padding: "2px 6px",
    borderRadius: "14px",
  },

  // Toggle button styles
  toggleButton: (
    disableHistorySidebar,
    isDarkTheme,
    radius,
    boxShadow,
    isMobile
  ) => ({
    position: "fixed",
    top: "40%",
    backgroundColor: disableHistorySidebar
      ? "#757575"
      : isDarkTheme
      ? "#414079"
      : "#f0f2f8",
    borderTopLeftRadius: radius,
    borderBottomLeftRadius: radius,
    right: 0,
    zIndex: 1200,
    padding: isMobile ? "0.5rem 0.3rem" : "0.625rem 0.375rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: boxShadow,
    height: "21vh",
    minHeight: "13dvh",
    minWidth: "3vh",
    width: "5vh",
    cursor: disableHistorySidebar ? "not-allowed" : "pointer",
    pointerEvents: disableHistorySidebar ? "none" : "auto",
    opacity: disableHistorySidebar ? 0.7 : 1,
  }),

  toggleIcon: (isMobile, isDarkTheme) => ({
    width: "3vh",
    height: "3dvh",
    backgroundColor: "#A8A9FF",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "none",
    color: isDarkTheme ? "#414079" : "#5D5FEF",
    // marginBottom: "0.5rem",
    padding: 0,
  }),

  toggleTextWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 1,
  },

  // Content styles
  contentContainer: (isMobile, isDarkTheme) => ({
    overflow: "auto",
    flex: 1,
    padding: isMobile ? "0.5rem" : "0.75rem",
    color: isDarkTheme ? "#fff" : "#333",
  }),

  section: (isMobile) => ({
    marginBottom: isMobile ? "0.75rem" : "1.25rem",
  }),

  heading: (isMobile, isDarkTheme) => ({
    fontSize: isMobile ? "1rem" : "1.25rem",
    margin: "0.5rem 0",
    color: isDarkTheme ? "#fff" : "#333",
  }),

  itemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  // Typography styles
  sidebarText: (isMobile) => ({
    fontSize: isMobile ? "0.875rem" : "1rem",
    whiteSpace: "nowrap",
    color: "#fff",
  }),

  toggleText: (isMobile, isDarkTheme) => ({
    transform: "rotate(-90deg)",
    fontWeight: "500",
    fontSize: "1.66dvh",
    textAlign: "center",
    whiteSpace: "nowrap",
    width: "auto",
    maxWidth: isMobile ? "15vh" : "18vh",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: isDarkTheme ? "#fff" : "#414079",
  }),
};

export default EhrPatientDataSidebar;
